import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { api } from "@shared/routes";

interface AlterarSenhaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usuarioId: string;
  usuarioNome: string;
  isOwnPassword: boolean; // true se é a própria senha, false se é redefinição por admin
  onSuccess?: () => void;
}

export function AlterarSenhaDialog({
  open,
  onOpenChange,
  usuarioId,
  usuarioNome,
  isOwnPassword,
  onSuccess,
}: AlterarSenhaDialogProps) {
  const { session } = useAuth();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    try {
      const endpoint = isOwnPassword
        ? api.usuarios.changePassword.path.replace(":id", usuarioId)
        : api.usuarios.resetPassword.path.replace(":id", usuarioId);

      const body = isOwnPassword
        ? {
            senhaAtual,
            novaSenha,
            confirmarSenha,
          }
        : {
            novaSenha,
            confirmarSenha,
          };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 400) {
          // Erro de validação
          setErrors({ submit: data.message || "Erro na validação" });
        } else if (res.status === 401) {
          setErrors({ senhaAtual: data.message || "Senha atual incorreta" });
        } else {
          setErrors({ submit: data.message || "Erro ao alterar senha" });
        }
        return;
      }

      toast({
        title: "Sucesso",
        description: data.message,
      });

      // Limpar formulário
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setErrors({ submit: "Erro ao conectar com servidor" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isOwnPassword ? "Alterar Senha" : `Redefinir Senha — ${usuarioNome}`}
          </DialogTitle>
          <DialogDescription>
            {isOwnPassword
              ? "Digite sua senha atual e a nova senha"
              : "Digite a nova senha para este usuário"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isOwnPassword && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Senha Atual
              </label>
              <Input
                type="password"
                placeholder="Digite sua senha atual"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                disabled={isLoading}
                required
              />
              {errors.senhaAtual && (
                <p className="text-xs text-red-600">{errors.senhaAtual}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Nova Senha
            </label>
            <Input
              type="password"
              placeholder="Digite a nova senha (mínimo 6 caracteres)"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              disabled={isLoading}
              required
              minLength={6}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Confirmar Senha
            </label>
            <Input
              type="password"
              placeholder="Confirme a nova senha"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              disabled={isLoading}
              required
              minLength={6}
            />
            {errors.confirmarSenha && (
              <p className="text-xs text-red-600">{errors.confirmarSenha}</p>
            )}
          </div>

          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Alterando..." : "Alterar Senha"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
